// =============================================================================
// USER PLAN HISTORY (Platform side) — drill-in for one B2C user's timeline
//
// Shown inline on the TenantDetail Users tab when a row is expanded.
// Fetches from the App-DB `user-subscription-history` edge function (gated
// by PROVISION_SECRET) — the data lives across the DB boundary, so this
// has to go through an edge function rather than a direct Supabase query.
// =============================================================================

import { useEffect, useState } from 'react';
import { Loader2, Download, ArrowDown, ArrowUp, X as XIcon, Sparkles, RefreshCw, Settings as SettingsIcon, Unlock, AlertTriangle } from 'lucide-react';
import { fetchUserSubscriptionHistory, resetUserFreePlanEligibility, type AppUserSubscriptionHistory } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const REASONS: Record<string, { label: string; icon: typeof ArrowDown; cls: string }> = {
  signup:       { label: 'Signed up',      icon: Sparkles,    cls: 'text-blue-600' },
  upgrade:      { label: 'Upgraded',       icon: ArrowUp,     cls: 'text-emerald-600' },
  downgrade:    { label: 'Downgraded',     icon: ArrowDown,   cls: 'text-amber-600' },
  cancel:       { label: 'Canceled',       icon: XIcon,       cls: 'text-red-600' },
  reactivate:   { label: 'Reactivated',    icon: RefreshCw,   cls: 'text-emerald-600' },
  renewal:      { label: 'Billing change', icon: RefreshCw,   cls: 'text-surface-500' },
  admin_change: { label: 'Plan change',    icon: SettingsIcon, cls: 'text-surface-500' },
};

export function UserPlanHistory({
  tenantId, userId, userLabel,
}: {
  tenantId: string;
  userId:   string;
  /** Used in the export filename — typically email or full name. */
  userLabel: string;
}) {
  const { isSuperAdmin } = useAuth();
  const [rows, setRows]       = useState<AppUserSubscriptionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  // ── Free-plan reset state ──────────────────────────────────────────
  // The drill-in already has the user's history — we infer "has used
  // free" from whether any historical row was on a free-priced plan.
  // After a successful reset, the next signup-to-free will work again.
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Has the user occupied a free plan at any point in their history?
  // The button only renders when this is true (and user is super admin).
  const hasUsedFree = rows.some(r => r.is_free);

  const handleResetFree = async () => {
    setResetting(true);
    setResetError(null);
    const result = await resetUserFreePlanEligibility(tenantId, userId);
    setResetting(false);
    if (!result.ok) {
      setResetError(result.error || 'Reset failed');
      return;
    }
    setResetSuccess(true);
    setShowResetConfirm(false);
    setTimeout(() => setResetSuccess(false), 4000);
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fetchUserSubscriptionHistory(tenantId, userId)
      .then(h => { if (alive) setRows(h); })
      .catch(e => { if (alive) setError(e?.message ?? String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tenantId, userId]);

  const exportCsv = () => {
    if (!rows.length) return;
    const headers = [
      'Plan', 'Plan key', 'Monthly price', 'Annual price', 'Currency',
      'Status', 'Billing', 'Change', 'Started', 'Ended',
    ];
    const escape = (v: unknown) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = rows.map(r => [
      r.plan_name ?? '—',
      r.plan_key ?? '',
      r.monthly_price ?? '',
      r.annual_price ?? '',
      r.currency ?? '',
      r.status,
      r.billing_period ?? '',
      REASONS[r.change_reason]?.label ?? r.change_reason,
      r.started_at,
      r.ended_at ?? '',
    ].map(escape).join(','));
    const csv  = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${userLabel.replace(/[^a-z0-9._-]+/gi, '-').toLowerCase()}-plan-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="px-6 py-4 flex items-center gap-2 text-xs text-surface-500">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading plan history…
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-4 text-xs text-red-700">Couldn't load plan history: {error}</div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="px-6 py-4 text-xs text-surface-500">No plan history yet for this user.</div>
    );
  }

  return (
    <div className="px-6 py-4 bg-surface-50 dark:bg-surface-100/30 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-surface-600">
          Plan History
        </p>
        <div className="flex items-center gap-3">
          {/* Super-admin only: clear free_plan_used_at so the user can
              return to the free tier (rare — used for billing waivers
              or undoing accidental signups). */}
          {isSuperAdmin && hasUsedFree && (
            <button
              onClick={() => { setResetError(null); setShowResetConfirm(true); }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:underline"
              title="Re-enable free plan for this user"
            >
              <Unlock className="w-3.5 h-3.5" /> Reset free-plan eligibility
            </button>
          )}
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {resetSuccess && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
          <Unlock className="w-3.5 h-3.5" />
          Free-plan eligibility re-enabled for {userLabel}.
        </div>
      )}

      {showResetConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !resetting && setShowResetConfirm(false)}
        >
          <div
            className="bg-surface-0 dark:bg-surface-100 rounded-xl shadow-xl max-w-md w-full p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="text-base font-semibold text-surface-900">Reset free-plan eligibility?</h4>
                <p className="text-xs text-surface-600 mt-1">
                  This will clear <span className="font-mono">free_plan_used_at</span> for{' '}
                  <span className="font-semibold">{userLabel}</span>, allowing them to switch back to the free tier.
                </p>
                <p className="text-[11px] text-surface-500 mt-2">
                  Use this only when there's a legitimate reason (billing waiver, mistaken signup).
                  The action is recorded in <span className="font-mono">user_subscription_history</span> on
                  the next plan change.
                </p>
              </div>
            </div>

            {resetError && (
              <div className="p-2.5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                {resetError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-surface-200">
              <button
                onClick={() => setShowResetConfirm(false)}
                disabled={resetting}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-surface-700 hover:bg-surface-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResetFree}
                disabled={resetting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
              >
                {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
                {resetting ? 'Resetting…' : 'Confirm reset'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ol className="relative pl-5 space-y-3">
        <span className="absolute left-1.5 top-1.5 bottom-1.5 w-px bg-surface-300" aria-hidden />
        {rows.map((r) => {
          const reason = REASONS[r.change_reason] ?? REASONS.admin_change;
          const Icon = reason.icon;
          return (
            <li key={r.id} className="relative">
              <span
                className={`absolute -left-3.5 top-1 w-2.5 h-2.5 rounded-full ring-2 ring-surface-50 ${
                  r.ended_at == null ? 'bg-emerald-500' : 'bg-surface-300'
                }`}
                aria-hidden
              />
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-surface-900">
                      {r.plan_name ?? 'Unknown plan'}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${reason.cls}`}>
                      <Icon className="w-3 h-3" /> {reason.label}
                    </span>
                    {r.is_free && (
                      <span className="text-[10px] font-medium text-surface-600 bg-surface-100 px-1.5 py-0.5 rounded">
                        FREE
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-surface-500 mt-0.5">
                    {new Date(r.started_at).toLocaleDateString()} →{' '}
                    {r.ended_at ? new Date(r.ended_at).toLocaleDateString()
                                : <span className="text-emerald-700 font-medium">Present</span>}
                    {r.billing_period && <> · {r.billing_period}</>}
                    <span className="ml-2 capitalize text-surface-400">· {r.status}</span>
                  </p>
                </div>
                {r.monthly_price != null && r.monthly_price > 0 && (
                  <span className="text-xs font-medium text-surface-700 shrink-0">
                    ${r.monthly_price}/{r.billing_period === 'annual' ? 'yr' : 'mo'}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
